import {
  AspectRatio,
  Card,
  Text,
  Stack,
  Inline,
  Box,
  Icon,
  Badge,
  Styled,
  Tiles,
  useResponsiveValue
} from '@front10/base-elements';
import * as React from 'react';
import ItemPrice from '../_commons/atoms/ItemPrice';
import { EventImage } from './EventImage';

interface Props {
  locationName: string;
  sessionType: string;
  icon?: string;
  date: string;
  sellingLabel?: string;
  availabilityLabel?: string;
  offerLabel?: string;
  title: string;
  price: string;
  ctaUrl: string;
  ctaLabel?: string;
  imageUrl: string;
  showMoreInfo?: boolean;
  description?: string;
  moreInfoLinkInteractionType?: string;
  moreInfoLink?: string;
  ctaSlot?: React.ReactElement | null;
}

export function EventDetailsCard({
  title,
  price,
  imageUrl,
  sessionType,
  icon,
  date,
  locationName,
  offerLabel,
  ctaSlot,
  description,
  sx
}: React.PropsWithChildren<Styled<Props>>) {
  const aspectRatio = useResponsiveValue([1.6, 1.6, 2.5]);
  return (
    <Card
      sx={{
        height: '100%',
        maxHeight: '100vh',
        position: 'relative',
        isolation: 'isolate',
        overflow: 'auto',
        '[data-fr-item-price]': {
          display: 'flex',
          alignItems: 'baseline',
          '[data-fr-promo-label]': {
            fontFamily: 'heading',
            fontSize: '15px',
            color: 'gray600',
            fontWeight: 600,
            marginRight: '16px'
          },
          '[data-fr-currency]': {
            fontSize: '22px',
            lineHeight: 1,
            fontWeight: 700,
            color: 'accent'
          }
        },
        ...sx
      }}
      variant="primary"
      data-fr-event-card
    >
      <Box sx={{ position: 'relative' }}>
        <AspectRatio ratio={aspectRatio}>
          <EventImage altText="" image={imageUrl} size="big" />
        </AspectRatio>
      </Box>
      <Stack pt="28px" px="32px" pb="38px" gap="3" alignY="space-between" sx={{ height: '100%' }}>
        <Inline align="start" alignY="center" gap={2}>
          <Badge>{offerLabel}</Badge>
          {icon ? <Icon icon={icon} size="1.2em" /> : null}
        </Inline>
        <Text variant="heading2" as="h3">
          {title}
        </Text>
        {price ? (
          <ItemPrice
            formattedTotalPrice={price}
            showCurrencyCode={false}
            showPriceDisclaimer={false}
            showCurrencySymbolSub={false}
          />
        ) : null}
        {description ? (
          <Text variant="fontSize5" as="div">
            <p>{description}</p>
          </Text>
        ) : null}
        <Tiles columns={[1, 1, 3]} rowGap={4}>
          <Stack gap="2" sx={{ gridColumn: 'span 2' }}>
            <Inline gap="2" alignY="center" variant="text.label">
              <Inline gap="1">
                <Icon
                  icon="calendar"
                  size="1.43em"
                  sx={{ flex: '0 0 auto', position: 'relative', top: '0.0625em' }}
                />
                <span>{date}</span>
              </Inline>
              <span>|</span>
              <span>{sessionType}</span>
            </Inline>
            <Inline gap="1" variant="text.label">
              <Icon
                icon="marker"
                size="1.43em"
                sx={{ flex: '0 0 auto', position: 'relative', top: '0.0625em' }}
              />
              <span>{locationName}</span>
            </Inline>
          </Stack>
          <Inline align="end">{ctaSlot}</Inline>
        </Tiles>
      </Stack>
    </Card>
  );
}
